import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import { Alert, Button, CircularProgress } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { useTranslate } from 'react-admin'
import AuthShell from './AuthShell'

interface VerifyEmailProps {
  token?: string
}

const VerifyEmail: React.FC<VerifyEmailProps> = ({ token }) => {
  const [message, setMessage] = useState('')
  const [isError, setIsError] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [isVerified, setIsVerified] = useState(false)
  const translate = useTranslate()

  // Check if already verified by backend (backend redirects with verified=true)
  const urlParams = new URLSearchParams(window.location.search)
  const alreadyVerified = urlParams.get('verified') === 'true'
  const actualToken = token || urlParams.get('token')

  useEffect(() => {
    const verifyEmail = async () => {
      // If backend already verified, just show success and redirect
      if (alreadyVerified) {
        setIsError(false)
        setIsVerified(true)
        setMessage(translate('pos.auth.verify_email.success_message'))
        setIsLoading(false)
        setTimeout(() => {
          window.location.href = '/vet-portal/#/login'
        }, 3000)
        return
      }

      // Otherwise, if we have a token, redirect to backend verify endpoint
      if (actualToken) {
        // Redirect to backend verification endpoint which will handle verification
        // and redirect back to this page with verified=true
        window.location.href = `/api/users/verify2/${actualToken}`
        return
      }

      // No token and not verified
      setIsError(true)
      setMessage(translate('pos.auth.verify_email.invalid_message'))
      setIsLoading(false)
    }

    verifyEmail()
  }, [actualToken, alreadyVerified, translate])

  const handleGoToLogin = () => {
    window.location.href = '/vet-portal/#/login'
  }

  if (isLoading) {
    return (
      <AuthShell
        icon={<CircularProgress size={36} />}
        title={translate('pos.auth.verify_email.verifying_title')}
        subtitle={translate('pos.auth.verify_email.verifying_body')}
      />
    )
  }

  return (
    <AuthShell
      icon={
        isError ? (
          <ErrorOutlineIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
        ) : (
          <CheckCircleOutlineIcon sx={{ fontSize: 40, color: 'success.main' }} />
        )
      }
      title={translate(
        isError ? 'pos.auth.verify_email.failed_title' : 'pos.auth.verify_email.success_title',
      )}
      subtitle={isError ? translate('pos.auth.verify_email.contact_support') : undefined}
    >
      {message && (
        <Alert severity={isError ? 'error' : 'success'} sx={{ mb: 3 }}>
          {message}
        </Alert>
      )}
      <Button variant="contained" onClick={handleGoToLogin} fullWidth sx={{ py: 1.25 }}>
        {translate('pos.auth.verify_email.go_to_login')}
      </Button>
    </AuthShell>
  )
}

export default VerifyEmail
